import type { ItemPacking, ItemType, TripLoadType } from '@hero/schema';
import { BAYAN_GOOD_TYPE_SNAPSHOT } from './bayan.good-types.data';
import {
  BAYAN_GOOD_TYPE_PARENTS,
  BAYAN_ITEM_UNITS,
  BAYAN_PACKAGING_UNITS,
  type BayanItemUnitId,
  type BayanPackagingUnitId,
} from './bayan.reference';

/** Bayan leaf for palletised bottled water (مياه معبأة). */
export const BAYAN_BOTTLED_WATER_GOOD_TYPE_ID = 166;

/**
 * Where a load goes when nothing else identifies it — 14 `Dry Fast-Moving
 * Consum` / `سلع استهلاكية سريعة الحركة الجافة`.
 *
 * Bayan has no "general cargo" node, so a document has to name *something*.
 * This is the most generic non-hazardous leaf in the taxonomy and the one a
 * dispatcher reaches for when the shipper said "بضاعة" and nothing more.
 *
 * It applies only when there is **no signal at all** about the cargo — see
 * {@link resolveBayanGoodType}. A load Hero could place in a branch but not
 * on a leaf (construction, petroleum, livestock) still blocks and asks,
 * because "which of these 20 building materials?" is a question the user can
 * answer, and filing rebar as consumer goods is not an answer at all.
 */
export const BAYAN_PLATFORM_DEFAULT_GOOD_TYPE_ID = 14;

const normalizeItemName = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[\u064B-\u0652\u0640]/g, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .trim();

const BOTTLED_WATER_KEYWORDS = [
  'مويه',
  'موية',
  'مياه',
  'ماء',
  'مياه معباه',
  'water',
  'bottled water',
  'mineral water',
];

/** Whether the load description is ordinary bottled water, not bulk/tanker. */
export const isBottledWaterItemName = (name: string | undefined): boolean => {
  if (!name?.trim()) return false;
  const normalized = normalizeItemName(name);
  return BOTTLED_WATER_KEYWORDS.some(keyword => normalized.includes(normalizeItemName(keyword)));
};

/**
 * The consonant skeleton of an Arabic word.
 *
 * Long vowels are the first thing to go when someone types quickly on a
 * phone: `بلاستك` for `بلاستيك`, `مويه` for `مياه`. Dropping ا / و / ي from
 * both sides before comparing makes those the same token, which is the
 * difference between recognising a load and asking the user to open a
 * dropdown of 151 government categories at a loading gate.
 *
 * The definite article goes too — the catalog says `المواد البلاستيكية`
 * and nobody types the article when naming their cargo.
 *
 * Latin text is returned unchanged: dropping vowels from `oils` leaves `ls`,
 * which collides with everything.
 */
const arabicSkeleton = (token: string): string => {
  if (!/[\u0600-\u06FF]/.test(token)) return token;
  return token.replace(/^ال/, '').replace(/[اوي]/g, '');
};

const tokenize = (value: string): string[] =>
  normalizeItemName(value)
    .split(/[\s,،/\-_()]+/)
    .filter(Boolean);

/**
 * Shortest token worth matching on.
 *
 * Below three characters an Arabic skeleton is mostly noise — `مواد`
 * reduces to `مد`, which would match half the catalog.
 */
const MIN_MATCH_LENGTH = 3;

/**
 * Catalog leaves, pre-tokenized once. Built lazily so importing this module
 * on a frontend that never issues a waybill costs nothing.
 */
type LeafTokens = {
  id: number;
  hazardous: boolean;
  tokens: string[];
  /** The Arabic and the English name, tokenized apart. */
  names: string[][];
};

let leafTokenIndex: LeafTokens[] | undefined;

const leafNameTokens = (value: string): string[] =>
  tokenize(value)
    .map(arabicSkeleton)
    .filter(token => token.length >= MIN_MATCH_LENGTH);

const getLeafTokenIndex = (): LeafTokens[] => {
  if (leafTokenIndex) return leafTokenIndex;

  leafTokenIndex = [];
  for (const node of BAYAN_GOOD_TYPE_SNAPSHOT.values()) {
    if (node.hasChildren) continue;
    const names = [leafNameTokens(node.nameArabic), leafNameTokens(node.nameEnglish)];
    const tokens = names.flat();

    if (tokens.length > 0) {
      leafTokenIndex.push({
        id: node.id,
        hazardous: node.parentId === BAYAN_GOOD_TYPE_PARENTS.dangerous_materials,
        tokens,
        names,
      });
    }
  }
  return leafTokenIndex;
};

/**
 * Find the Bayan leaf whose own name best describes this cargo.
 *
 * Sits between the Hero `ItemType` map and the Org default, and exists
 * because that map is a hand-maintained middle layer over 24 coarse Hero
 * types, while ELM's catalog has 151 leaves with real Arabic names. A load
 * of `أسفنج` or `تبغ` or `كتب` has an obvious home in the taxonomy and no
 * home at all in the keyword list, and every material that gets added to
 * the world is one more the keyword list does not know about.
 *
 * Matching is prefix-on-skeleton in both directions, so `بلاستك` finds
 * `المواد البلاستيكية` and `بلاستيكية` would too. Longest match wins, so a
 * name that hits both a generic and a specific leaf takes the specific one.
 *
 * Returns `undefined` rather than a weak guess — the caller has a default
 * for that, and it is the caller that knows whether defaulting is allowed.
 */
export const matchBayanGoodTypeByName = (
  name: string | undefined,
  /**
   * Hazardous leaves (parent 35) are matched only when asked for: each one
   * needs the load's UN number, and "matching" one without it only swaps a
   * clear blocker for an undeclared hazardous shipment.
   */
  options: { hazardous?: boolean } = {},
): number | undefined => {
  if (!name?.trim()) return undefined;

  const itemTokens = tokenize(name)
    .map(arabicSkeleton)
    .filter(token => token.length >= MIN_MATCH_LENGTH);
  if (itemTokens.length === 0) return undefined;

  const tokenHit = (leafToken: string, itemToken: string) =>
    leafToken.startsWith(itemToken) || itemToken.startsWith(leafToken);

  let best: { id: number; score: number } | undefined;

  for (const leaf of getLeafTokenIndex()) {
    if (leaf.hazardous !== Boolean(options.hazardous)) continue;
    // A hazard class is named in full or not at all: «سوائل» is not
    // «السوائل اللهوبة».
    if (
      leaf.hazardous &&
      !leaf.names.some(
        words =>
          words.length > 0 &&
          words.every(word => itemTokens.some(itemToken => tokenHit(word, itemToken))),
      )
    ) {
      continue;
    }
    for (const leafToken of leaf.tokens) {
      for (const itemToken of itemTokens) {
        if (!tokenHit(leafToken, itemToken)) continue;

        const score = Math.min(leafToken.length, itemToken.length);
        if (!best || score > best.score) best = { id: leaf.id, score };
      }
    }
  }

  return best?.id;
};

const isDangerousGoodTypeId = (goodTypeId: number | undefined): boolean => {
  if (!goodTypeId) return false;
  return (
    BAYAN_GOOD_TYPE_SNAPSHOT.get(goodTypeId)?.parentId ===
    BAYAN_GOOD_TYPE_PARENTS.dangerous_materials
  );
};

/**
 * How much we trust a Hero → Bayan good-type guess.
 *
 * - `exact`               — the Bayan taxonomy has a 1:1 node for the Hero type.
 * - `approximate`         — nearest sensible node; fine to submit, but an Org
 *                           that cares should override it.
 * - `requires_selection`  — Hero's type is too coarse to pick a leaf node
 *                           (the Bayan parent has children and the guide
 *                           requires the child). The caller MUST supply an
 *                           explicit good type; validation fails otherwise.
 *
 * The third case is deliberate: submitting an arbitrary leaf (e.g. calling
 * every construction load "Concrete") would put wrong data on a government
 * document. Better to block and ask.
 */
export type BayanGoodTypeConfidence = 'exact' | 'approximate' | 'requires_selection';

export type BayanGoodTypeGuess = {
  /** Undefined when `confidence` is `requires_selection`. */
  goodTypeId?: number;
  confidence: BayanGoodTypeConfidence;
  /**
   * The branch the answer lives under, when we know it but not which child.
   *
   * "Pick a cargo type" against 159 leaves is a worse question than "pick one
   * of these 20 construction materials", so the picker narrows to this branch
   * and the blocker message can name it.
   */
  branchId?: number;
};

/**
 * Offline Hero `ItemType` → Bayan good-type map.
 *
 * Used both as the default guess and as the fallback when the live
 * `/lookup/good-types` call is unavailable, so a lookup outage never blocks
 * issuance for the types we can map confidently.
 *
 * Ids are verified against {@link BAYAN_GOOD_TYPE_SNAPSHOT} by
 * `bayan.good-types.spec.ts`, which asserts every id here exists and is a
 * leaf. That guard exists because this map was originally transcribed from
 * the V7 guide's § 5.9 appendix and had rotted badly against the live
 * taxonomy: it pointed `liquid` at node 1, which ELM has since removed, and
 * `plastic` / `perfume` / `beauty` / `furniture` at node 5, which has since
 * grown children and can therefore no longer be submitted. Six of ten Hero
 * types could not produce a waybill. Do not add an id here by hand without
 * re-running the snapshot.
 */
export const BAYAN_GOOD_TYPE_FALLBACK: Readonly<Record<ItemType, BayanGoodTypeGuess>> = {
  // ─── Exact leaves ───────────────────────────────────────────────────────
  // 136 Chemicals materials is a top-level leaf, and it is where ordinary
  // chemical cargo belongs — detergent, fertiliser, paint, industrial
  // powders. Only UN-regulated goods go under 35 Dangerous Materials, which
  // is what `dangerous_materials` is for.
  chemicals: { goodTypeId: 136, confidence: 'exact' },
  plastic: { goodTypeId: 135, confidence: 'exact' },
  perfume: { goodTypeId: 192, confidence: 'exact' },
  medical_supplies: { goodTypeId: 156, confidence: 'exact' },
  medicines: { goodTypeId: 149, confidence: 'exact' },
  electronics: { goodTypeId: 132, confidence: 'exact' },
  clothing: { goodTypeId: 147, confidence: 'exact' },
  textiles: { goodTypeId: 160, confidence: 'exact' },
  paper: { goodTypeId: 151, confidence: 'exact' },
  cleaning_materials: { goodTypeId: 163, confidence: 'exact' },

  // ─── Nearest sensible leaf ──────────────────────────────────────────────
  // 14 Dry Fast-Moving Consum, upgraded to 15 Cold Fast-Moving Consum for a
  // temperature-controlled load — see `resolveBayanGoodType`.
  food: { goodTypeId: 14, confidence: 'approximate' },
  // Cosmetics have no node of their own; they are fast-moving consumer goods.
  beauty: { goodTypeId: 14, confidence: 'approximate' },
  // 133 Furniture splits into 145 Used / 146 New. New is the ordinary freight
  // case; a used-furniture move should override.
  furniture: { goodTypeId: 146, confidence: 'approximate' },
  // 129 Spares splits into 130 Used / 134 Tires / 165 New.
  spare_parts: { goodTypeId: 165, confidence: 'approximate' },
  // 172 Electrical Appliances splits by appliance; 178 is ELM's own catch-all.
  appliances: { goodTypeId: 178, confidence: 'approximate' },
  // 141 Waste material's only child is 168 Medical Waste, which is far too
  // specific for general waste. 162 Remnants is the generic leaf.
  waste: { goodTypeId: 162, confidence: 'approximate' },

  // ─── Too coarse to pick a leaf ──────────────────────────────────────────
  // 34 Building And Construction Items — 20 children (Concrete, Steel,
  // Glass, Cement, Sand, …) with no generic one.
  construction_materials: { confidence: 'requires_selection', branchId: 34 },
  // 10 Petroleum Products — 12 children, and declaring Diesel as Jet Fuel on
  // a government document is not a guess worth making.
  petroleum: { confidence: 'requires_selection', branchId: 10 },
  // 4 Agricultural — forage/grain vs fertilizers.
  agriculture: { confidence: 'requires_selection', branchId: 4 },
  // 11 LIVE ANIMALS — birds vs cattle vs camels/horses.
  livestock: { confidence: 'requires_selection', branchId: 11 },
  // 6 Mining or Fossilization Materials — 60 children, one per mineral.
  mining: { confidence: 'requires_selection', branchId: 6 },
  // 13 Vehicle Transportation — 5 children, and every one of them also
  // requires the transported vehicle's chassis or sequence number, which
  // Hero does not capture. Blocked downstream regardless of the leaf picked.
  vehicles: { confidence: 'requires_selection', branchId: 13 },
  // 35 Dangerous Materials — 9 UN hazard classes, each requiring the
  // item's `dangerousCode`. The class comes from the load's name.
  dangerous_materials: { confidence: 'requires_selection', branchId: 35 },

  // ─── No home in the taxonomy ────────────────────────────────────────────
  // Deprecated. "Liquid" is a state, not a cargo category, and the node it
  // used to map to (1) no longer exists. Kept in the schema so orders
  // already carrying it still parse; hidden from the pickers.
  liquid: { confidence: 'requires_selection' },
  other: { confidence: 'requires_selection' },
};

/**
 * Keywords that mean the load *is* a shipping container (حاوية / كنتنر /
 * container), not loose goods. Drivers type these freely on WhatsApp —
 * "حاوية 40 قدم", "كنتينر", "40ft container".
 */
const CONTAINER_NAME_KEYWORDS = [
  'حاويه',
  'حاوية',
  'كنتنر',
  'كنتينر',
  'كونتينر',
  'container',
  'contaner', // common typo
  '40ft',
  '40 قدم',
  '20ft',
  '20 قدم',
];

/** Whether the cargo description names a shipping container. */
export const isContainerItemName = (name: string | undefined): boolean => {
  if (!name?.trim()) return false;
  const normalized = normalizeItemName(name);
  return CONTAINER_NAME_KEYWORDS.some(keyword => normalized.includes(normalizeItemName(keyword)));
};

/**
 * ISO-ish external dimensions for a shipping container, Bayan guide format
 * (`"10 x 10 x 10"`). Parsed from the cargo name when the user said
 * «40 قدم» / «20ft»; defaults to 40ft (the common KSA flatbed haul).
 */
export const resolveContainerDimensions = (name: string | undefined): string => {
  const normalized = normalizeItemName(name ?? '');
  if (
    /\b20\s*(ft|قدم|foot|feet)\b/.test(normalized) ||
    normalized.includes('20ft') ||
    normalized.includes('20 قدم')
  ) {
    return '6.1 x 2.4 x 2.6';
  }
  // 40ft and unspecified container size
  return '12.2 x 2.4 x 2.6';
};

export type BayanResolvedItemUnits = {
  /** Weight unit for `unitId` — Ton or KG after the mapper's mass conversion. */
  unitId: BayanItemUnitId;
  packagingUnitId?: BayanPackagingUnitId;
  /** Required by Bayan when `packagingUnitId` is set. */
  dimensions?: string;
};

/**
 * A standard loaded pallet, Bayan guide format: the 1.2 × 1.0 m footprint
 * common on Saudi trucks, 1.5 m high. Hero does not ask for pallet sizes,
 * and Bayan refuses a packaging unit without dimensions.
 */
export const BAYAN_STANDARD_PALLET_DIMENSIONS = '1.2 x 1.0 x 1.5';

/**
 * Resolve Bayan weight + packaging units for an order item (V9 § 5.1 / 6.7).
 *
 * - Container loads → `unitId=Ton` + `packagingUnitId=Container` + dimensions
 * - Pallets → `unitId=Ton` + `packagingUnitId=Pallet` + the standard pallet
 *   dimensions, so the وثيقة reads طبلية × quantity. Any `packagingUnitId`
 *   makes `dimensions` mandatory — without them `[dimensions is required]`
 *   (staging, 5 Oct 2026).
 * - Everything else → `unitId=Ton`; the count rides on `quantity`. V9
 *   `unitId` is a weight unit only (2 Ton, 5 KG).
 */
export const resolveBayanItemUnits = (args: {
  packing?: ItemPacking;
  loadCategory?: TripLoadType;
  itemName?: string;
}): BayanResolvedItemUnits => {
  const isContainer = args.loadCategory === 'container' || isContainerItemName(args.itemName);

  if (isContainer) {
    return {
      unitId: BAYAN_ITEM_UNITS.ton,
      packagingUnitId: BAYAN_PACKAGING_UNITS.container,
      dimensions: resolveContainerDimensions(args.itemName),
    };
  }

  if (args.packing === 'pallet') {
    return {
      unitId: BAYAN_ITEM_UNITS.ton,
      packagingUnitId: BAYAN_PACKAGING_UNITS.pallet,
      dimensions: BAYAN_STANDARD_PALLET_DIMENSIONS,
    };
  }

  return { unitId: BAYAN_ITEM_UNITS.ton };
};

/**
 * @deprecated Prefer {@link resolveBayanItemUnits}. Kept for call sites that
 * only need the weight/legacy unit id.
 */
export const resolveBayanUnitId = (args: {
  packing?: ItemPacking;
  loadCategory?: TripLoadType;
  itemName?: string;
}): BayanItemUnitId => resolveBayanItemUnits(args).unitId;

/**
 * Dry leaf → its cold-chain counterpart.
 *
 * Bayan splits fast-moving consumer goods and health care into a dry and a
 * chilled node each. Declaring a reefer load as dry is wrong on a government
 * document and is exactly the kind of mismatch an ELM audit looks for, so a
 * temperature-controlled order upgrades the leaf.
 */
const COLD_CHAIN_LEAF: Readonly<Record<number, number>> = {
  14: 15, // Dry Fast-Moving Consum → Cold Fast-Moving Consum
  16: 17, // Dry Health Care        → Cold Health Care
};

/**
 * Resolve the good type for an item, honouring explicit overrides.
 *
 * Precedence, most trusted first:
 *
 * 1. `overrideGoodTypeId` — someone picked this node by hand.
 * 2. Bottled water, by name — its own leaf, and it must not inherit a default.
 * 3. Hero `ItemType` → {@link BAYAN_GOOD_TYPE_FALLBACK}, curated per type.
 * 4. {@link matchBayanGoodTypeByName} against ELM's own catalog names.
 * 5. `orgDefaultGoodTypeId` — what this carrier usually ships.
 * 6. {@link BAYAN_PLATFORM_DEFAULT_GOOD_TYPE_ID}, only when 1–5 all missed.
 *
 * The Org default sits near the bottom on purpose. It is a floor for loads
 * Hero cannot place, not a blanket override — a carrier whose default is
 * "Concrete" still ships the occasional pallet of food, and that pallet
 * should not be declared as concrete.
 *
 * A load named after a hazard class, or typed hazardous, never reaches
 * steps 3–6: it files under that class, or blocks on picking one.
 */
export const resolveBayanGoodType = (args: {
  itemType?: ItemType;
  /** Load description — used to recognise bottled water before the Org default. */
  itemName?: string;
  overrideGoodTypeId?: number;
  orgDefaultGoodTypeId?: number;
  /** Chilled or frozen order — upgrades a dry leaf to its cold counterpart. */
  isTemperatureControlled?: boolean;
}): BayanGoodTypeGuess => {
  if (args.overrideGoodTypeId) {
    // An explicit pick is never second-guessed, cold chain included: the
    // user chose this node on a government document.
    return { goodTypeId: args.overrideGoodTypeId, confidence: 'exact' };
  }

  // Before the offline map or Org default — palletised water is its own Bayan
  // leaf and must never inherit a default that might sit under Dangerous Materials.
  if (isBottledWaterItemName(args.itemName)) {
    return { goodTypeId: BAYAN_BOTTLED_WATER_GOOD_TYPE_ID, confidence: 'exact' };
  }

  const guess = args.itemType ? BAYAN_GOOD_TYPE_FALLBACK[args.itemType] : undefined;

  // A load named after a hazard class («مواد مشعة») is hazardous whatever
  // its Hero type says, and files under that class's leaf. The mapper
  // blocks it until the item carries its UN number. Hazardous cargo never
  // inherits a default: a carrier whose default is "Concrete" would
  // otherwise ship explosives on a waybill declaring concrete.
  const typedHazardous = guess?.branchId === BAYAN_GOOD_TYPE_PARENTS.dangerous_materials;
  const plainByName = matchBayanGoodTypeByName(args.itemName);
  const hazardousByName =
    typedHazardous || !plainByName
      ? matchBayanGoodTypeByName(args.itemName, { hazardous: true })
      : undefined;
  if (hazardousByName) return { goodTypeId: hazardousByName, confidence: 'approximate' };
  if (typedHazardous) {
    return {
      confidence: 'requires_selection',
      branchId: BAYAN_GOOD_TYPE_PARENTS.dangerous_materials,
    };
  }

  if (guess?.goodTypeId) {
    const cold = args.isTemperatureControlled ? COLD_CHAIN_LEAF[guess.goodTypeId] : undefined;
    return cold ? { ...guess, goodTypeId: cold } : guess;
  }

  const orgDefaultIsDangerous = isDangerousGoodTypeId(args.orgDefaultGoodTypeId);

  // What the shipper actually called the load, matched against ELM's own
  // names. Ahead of the Org default because "أسفنج" is a better description
  // of this load than "whatever this carrier usually ships", and behind the
  // Hero type map because that map is curated and this is inference.
  if (plainByName) return { goodTypeId: plainByName, confidence: 'approximate' };

  if (args.orgDefaultGoodTypeId && !orgDefaultIsDangerous) {
    return { goodTypeId: args.orgDefaultGoodTypeId, confidence: 'approximate' };
  }

  // Last resort, and deliberately narrow: only when nothing at all placed
  // this load — no override, no recognised name, no Hero type, no Org
  // default. A `branchId` means Hero DID place it, just not precisely
  // enough, and "pick one of these 20 building materials" is a question the
  // user can answer in one word. Defaulting that to consumer goods would
  // put a wrong classification on a government document to save a single
  // round trip.
  if (!guess?.branchId) {
    return { goodTypeId: BAYAN_PLATFORM_DEFAULT_GOOD_TYPE_ID, confidence: 'approximate' };
  }

  return { confidence: 'requires_selection', branchId: guess?.branchId };
};
